/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useRef, useState } from "react";

// react-router-dom components
import { Link, Navigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import PinInput from "../components/PinInput";

// Authentication layout components
import BasicLayout from "examples/LayoutContainers/BasicLayout";

// Images
import bgImage from "assets/images/bg-reset-cover.jpeg";

// Router
import { useNavigate } from "react-router-dom";
import { useApi, useUser } from "../../../context/api.js";

const ERROR_MESSAGES = {
  0: "Erreur inattendue.",
  1: "Veuillez saisir votre nom d'utilisateur.",
  2: "Utilisateur non trouvé.",
  3: "Code invalide.",
  4: "Veuillez saisir votre nouveau mot de passe.",
  5: "Veuillez confirmer votre nouveau mot de passe.",
  6: "Les mots de passe ne concordent pas.",
  7: "Impossible de réinitialiser le mot de passe.",
};

function ResetPassword() {
  const navigate = useNavigate();
  const api = useApi();
  const user = useUser();

  const [step, setStep] = useState(1);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ open: false });
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  const handleSubmit = (event) => {
    event.preventDefault();

    if (step === 1) {
      if (!username) {
        setError({ open: true, code: 1 });

        return;
      }

      setLoading(true);
      api
        .resetPassword(username)
        .then((res) => {
          if (res) {
            setStep(2);
          } else {
            //setError({ open: true, code: 2 });
            // For security reasons, we display the code validation screen
            setStep(2);
          }
          setLoading(false);
        })
        .catch((error) => {
          setError({ open: true, code: error.code });
          setLoading(false);
        });
    } else if (step === 2) {
      setLoading(true);
      api
        .validateResetCode(username, pin)
        .then((res) => {
          if (res) {
            setStep(3);
          } else {
            setError({ open: true, code: 3 });
          }
          setLoading(false);
        })
        .catch((error) => {
          setError({ open: true, code: error.code });
          setLoading(false);
        });
    } else if (step === 3) {
      if (!password) {
        setError({ open: true, code: 4 });

        return;
      }

      if (!passwordConfirmation) {
        setError({ open: true, code: 5 });

        return;
      }

      if (password !== passwordConfirmation) {
        setError({ open: true, code: 6 });

        return;
      }

      setLoading(true);
      api
        .changePassword(username, password, null, pin)
        .then((res) => {
          if (res) {
            navigate("/login", { state: { username } });
          } else {
            setError({ open: true, code: 7 });
          }
          setLoading(false);
        })
        .catch((error) => {
          setError({ open: true, code: error.code || 0 });
          setLoading(false);
        });
    }
  };

  const closeError = () => setError((e) => ({ ...e, open: false }));

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" my={1}>
            Réinitialiser mon mot de passe
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            {step === 1 && (
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Nom d'utilisateur"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  fullWidth
                  disabled={loading}
                />
              </MDBox>
            )}
            {step === 2 && (
              <PinInput
                value={pin}
                onChange={(e) => setPin(e.currentTarget.value)}
                disabled={loading}
              />
            )}
            {step === 3 && (
              <>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Mot de passe"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    disabled={loading}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Confirmation"
                    name="passwordConfirmation"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    fullWidth
                    disabled={loading}
                  />
                </MDBox>
              </>
            )}
            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                type="submit"
                fullWidth
                disabled={
                  loading || (step === 1 && !username) || (step === 2 && !pin.match(/^[0-9]{6}$/))
                }
              >
                {step === 1 && "Envoyer un code"}
                {step === 2 && "Valider"}
                {step === 3 && "Modifier mon mot de passe"}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Mot de passe retrouvé&nbsp;?{" "}
                <MDTypography
                  component={Link}
                  to="/login"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Cliquez ici
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={closeError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert variant="filled" severity="error">
          {ERROR_MESSAGES[error.code] || ERROR_MESSAGES[400]}
        </Alert>
      </Snackbar>
    </BasicLayout>
  );
}

export default ResetPassword;
